import React, { FunctionComponent, ChangeEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton } from 'components';
import { Link, Text } from 'components/chroma';
import config from 'shared/config';

import { SingleFieldStepProps } from 'scenes/common/types';
import { InputQuestion } from 'scenes/common/components/Questions';
import {
  APOST_EMAIL_MSG,
  checkEmailForSingleQuote,
} from 'scenes/common/helpers/validation';
import styled from 'styled-components';

const CONTACT_EMAIL_ADDRESS = 'contactEmailAddress';
export const ERROR_MESSAGE = 'Please enter a valid email address';
const VALID_EMAIL_PATTERN = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;
const MIN_LENGTH = 5;

const HelpTextWrapper = styled.div`
  margin-bottom: 0.5rem;
`;

const HELP_TEXT = () => (
  <>
    <Text margin={{ bottom: 1 }}>
      To keep our premiums low we will send all your policy and renewal
      documents and notifications by email.
    </Text>
    <HelpTextWrapper>
      You can view all of your documents at any time using our online{' '}
      <Link
        href={config.ossUrl}
        target="_blank"
        margin={{ bottom: 0, top: 0 }}
        rel="noopener noreferrer"
        inline
      >
        Insurance Service Centre.
      </Link>
    </HelpTextWrapper>
  </>
);

const ContactEmailAddressStep: FunctionComponent<Pick<
  SingleFieldStepProps<string>,
  'defaultStateValue' | 'onNext' | 'question'
>> = ({ defaultStateValue, onNext, question }) => {
  const { formState: hooksForm, handleSubmit, errors, control } = useForm({
    mode: 'onChange',
  });

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      onChange(event.target.value?.trim());
    },
    []
  );

  return (
    <form onSubmit={handleSubmit(onNext)} autoComplete="on">
      <InputQuestion
        errors={errors}
        control={control}
        stateKey={CONTACT_EMAIL_ADDRESS}
        defaultValue={defaultStateValue}
        changeHandler={handleChange}
        autoCompleteName="email"
        ariaRequired
        validationRules={{
          required: true,
          minLength: MIN_LENGTH,
          pattern: {
            value: VALID_EMAIL_PATTERN,
            message: ERROR_MESSAGE,
          },
          validate: (email: string): string | boolean => {
            if (checkEmailForSingleQuote(email)) {
              return APOST_EMAIL_MSG;
            }
            return true;
          },
        }}
        helpText={HELP_TEXT()}
        question={question}
        placeholder="e.g. name@company.com"
      />
      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default ContactEmailAddressStep;
